// Support page (Story #4613, Streams 8a/8b) — route /support.
//   • ?ticket={id}  → the requester ticket thread/detail (mockup 03)
//   • ?report=1     → New Request pre-filled from a captured crash record
//   • ?new=1        → a blank New Request form (in-page)
//   • (default)     → My Support Tickets list (mockup 02)
// Notification deep-links target /support?ticket={id} (see constants/notificationTypes).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import PageHeader from '../components/ui/PageHeader'
import { useAuth } from '../contexts/AuthContext'
import { listMyTickets } from '../services/support'
import type { SupportTicket } from '../types/support'
import MyTicketsList from '../components/support/MyTicketsList'
import TicketThread from '../components/support/TicketThread'
import NewRequestForm from '../components/support/NewRequestForm'
import { clearCrashReport, crashReportPrefill } from '../components/support/crashReport'

export default function Support() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const ticketId = params.get('ticket')
  const isReport = params.get('report') === '1'
  const isNew = params.get('new') === '1' || isReport

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listMyTickets()
      .then((r) => {
        setTickets(r.tickets ?? [])
        setLoading(false)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Could not load your tickets')
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const goList = useCallback(() => setParams({}, { replace: true }), [setParams])
  const openTicket = useCallback(
    (id: string) => setParams({ ticket: id }, { replace: false }),
    [setParams],
  )

  // Crash-report prefill is computed once and the record cleared so a later
  // manual "New request" isn't unexpectedly pre-filled from a stale crash.
  const prefill = useMemo(() => {
    if (!isReport) return undefined
    const p = crashReportPrefill()
    clearCrashReport()
    return p
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReport])

  // ── Detail view ────────────────────────────────────────────────────────────
  if (ticketId) {
    return (
      <div className="content-bg">
        <TicketThread
          ticketId={ticketId}
          requesterUserId={user?.id}
          onBack={goList}
          onChanged={load}
        />
      </div>
    )
  }

  // ── New request view (in-page, incl. error-report path) ─────────────────────
  if (isNew) {
    return (
      <div className="content-bg">
        <PageHeader
          title={isReport ? 'Report a problem' : 'New support request'}
          description="Tell us what’s happening — we usually respond within 1 business day (AEDT)."
        />
        <div className="max-w-2xl rounded-2xl border border-iflow-900/10 bg-white/70 p-5">
          <NewRequestForm
            userId={user?.id}
            myTickets={tickets}
            prefill={prefill}
            onSubmitted={(t) => openTicket(t.ticket_id)}
            onCancel={goList}
            onOpenTicket={openTicket}
          />
        </div>
      </div>
    )
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div className="content-bg">
      <PageHeader
        title="My Support Tickets"
        description="Track your requests and reply to our support team — all inside the portal."
      />
      {loading ? (
        <div className="flex items-center gap-2 py-10 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Loading your tickets…
        </div>
      ) : error ? (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</p>
      ) : (
        <MyTicketsList
          tickets={tickets}
          requesterUserId={user?.id}
          onOpen={openTicket}
          onNew={() => setParams({ new: '1' }, { replace: false })}
        />
      )}
    </div>
  )
}
